'use client';

import { WalletName, WalletReadyState } from '@solana/wallet-adapter-base';
import React, { useState } from 'react';
import { ChevronRight } from '@velocity-exchange/icons';
import useWalletsToDisplay, {
	WalletToDisplayInfo,
} from 'src/hooks/useWalletsToDisplay';
import { VelocityWalletName } from 'src/constants/wallets';
import { openUrl } from 'src/utils/browser';
import { getAppUrl } from 'src/constants/site';
import MarketIcon from '../Utils/MarketIcon';
import { WALLET_BUTTON_ICON_CLASSNAME, WalletButton } from './WalletButton';
import { EmailWalletsList } from './EmailWalletsList';
import ConnectWalletModalButton from 'src/components/Modals/Onboarding/ConnectWalletModal/ConnectWalletModalButton';

export const MoreWalletsCta = (props: {
	onClick: () => void;
	wallets: WalletToDisplayInfo[];
}) => {
	return (
		<button
			className="group flex w-full items-center justify-between text-text-secondary hover:text-text-default"
			onClick={props.onClick}
		>
			<span className="typo-body-sm">More wallets</span>
			<ChevronRight size={20} />
		</button>
	);
};

const OtherWalletsDropdown = (props: {
	wallets: WalletToDisplayInfo[];
	onChooseWallet?: (selection: string) => void;
}) => {
	const [expanded, setExpanded] = useState(false);

	const toggleExpanded = () => {
		setExpanded(!expanded);
	};

	return (
		<div className="w-full">
			<MoreWalletsCta onClick={toggleExpanded} wallets={props.wallets} />

			{/* grid-template-rows animates without measuring the content's height. */}
			<div
				className="grid w-full transition-[grid-template-rows] duration-300"
				style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
			>
				<div className="flex flex-col gap-y-md overflow-hidden pt-4">
					<EmailWalletsList
						onChooseWallet={(walletName: VelocityWalletName) =>
							props.onChooseWallet?.(walletName as string)
						}
					/>
					{props.wallets.map((walletProvider, index) => (
						<WalletButton
							onChooseWallet={() =>
								props.onChooseWallet?.(
									walletProvider.provider.adapter.name ?? ''
								)
							}
							wallet={walletProvider}
							key={(walletProvider.provider.adapter.name ?? '') + index}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

const ConnectWalletButtonsList = (props: {
	showUndetectedWallets?: boolean;
	numberOfWalletsToShow?: number;
	onSelectedWallet?: (walletName: VelocityWalletName) => void;
}) => {
	const { walletsToShow, hiddenWallets } = useWalletsToDisplay(
		props.numberOfWalletsToShow
	);

	const handleSelectWallet = (name: VelocityWalletName) => {
		props.onSelectedWallet?.(name);
	};

	const hasNoInstalledWallet =
		walletsToShow.length === 0 ||
		!walletsToShow.find(
			(wallet) => wallet.provider.readyState === WalletReadyState.Installed
		);

	return (
		<>
			<div className="mb-4 typo-body-sm">Select a wallet</div>

			{hasNoInstalledWallet && (
				<ConnectWalletModalButton
					onClick={() => openUrl(getAppUrl('/learn/best-solana-wallets'))}
				>
					<div className={WALLET_BUTTON_ICON_CLASSNAME}>
						<MarketIcon marketSymbol="SOL" className="size-full" />
					</div>
					Get a Solana Wallet
				</ConnectWalletModalButton>
			)}

			{/* Show regular Wallets to Show */}
			{walletsToShow.map((walletProvider) => (
				<WalletButton
					wallet={walletProvider}
					onChooseWallet={() =>
						handleSelectWallet(walletProvider.provider.adapter.name)
					}
					key={walletProvider.provider.adapter.name}
				/>
			))}

			{!props.showUndetectedWallets && hiddenWallets.length > 0 && (
				<OtherWalletsDropdown
					wallets={hiddenWallets}
					onChooseWallet={(walletName) =>
						handleSelectWallet(walletName as WalletName)
					}
				/>
			)}
		</>
	);
};

export default React.memo(ConnectWalletButtonsList);
